import i18nFactory from '@yandex-int/i18n';
import { filter, find, get, map, reduce, sortBy } from 'lodash';
import React from 'react';
import { Action } from 'redux';

import { openCryptaBehavioralEditor } from '../../actions/targetings/cryptaBehavioral';
import { openCryptaGroupFamilyEditor } from '../../actions/targetings/cryptaGroupFamily';
import { openCryptaSocialDemoEditor } from '../../actions/targetings/cryptaSocialDemo';
import { openInterestsTargetingEditor } from '../../actions/targetings/interestsEditor';
import { CryptaSegmentGroup } from '../../components/CryptaSocialDemo/CryptaSocialDemo';
import {
    InterestsIdsType,
    InterestsSetType,
    InterestsTreeType
} from '../../components/InterestsSetEditor/InterestsSetEditor';
import { AudienceTargetingsType } from '../../typings/audienceTargetings';
import {
    SegmentCategory,
    SegmentCategoryText,
    SegmentSelector
} from '../../typings/crypta-segment-selector';
import { CryptaSegment } from '../../typings/cryptaSegments';
import { CryptaInterestTypeWeb, WebGoalType } from '../../typings/graphql-grid';
import {
    DataForEditGroups,
    EditGroupData
} from '../../typings/groupsEditState';
import { TextPredictor } from '../../typings/predictor';
import {
    RetargetingConditionData,
    RetargetingConditionDataRulesGroup,
    RetargetingConditionRuleType
} from '../../typings/retargeting';
import { UserProfileRetargetingConditionGoalType } from '../../typings/userProfileRetargeting';
import { Bem } from '../Bem/Bem';
import { getRetargetingConditionTypeText } from '../RetargetingHelpers/RetargetingHelpers';
import { getUserProfileConditionTitle } from '../UserProfileHelpers/UserProfileHelpers';

import * as keyset from './CryptaHelpers.i18n';

const i18n = i18nFactory(keyset);

const SIGNS_COMPACT = 7; // количество знаков добовляемых к строке (,)( )( )(·)( )(:)( )

export const mapRawCryptaSegmentsToSegmentsGroup = (
    segmentsGroupProps: CryptaSegment[]
): CryptaSegmentGroup => {
    const fieldsForSort = ['parentId'];

    // группируем категории по алфавиту только для family
    if (
        segmentsGroupProps.length &&
        segmentsGroupProps[0].type === WebGoalType.family
    ) {
        fieldsForSort.push('name');
    }

    return reduce(
        sortBy(segmentsGroupProps, fieldsForSort),
        (segmentsGroup: SegmentCategory[], segment: CryptaSegment) => {
            if (!Number(segment.parentId)) {
                segmentsGroup[segment.id] = {
                    title: segment.name,
                    canSelectAll: segment.canSelectAll,
                    selectors: []
                };
            } else if (segment.parentId && segmentsGroup[segment.parentId]) {
                segmentsGroup[segment.parentId].selectors.push({
                    id: String(segment.id),
                    name: segment.name
                });
            }

            return segmentsGroup;
        },
        {}
    );
};

export const getCheckedSegmentsVal = (
    segment: SegmentCategory,
    val: keyof SegmentSelector,
    checkedSegments: string[]
): string[] => {
    const checkedSegmentList = filter(
        segment.selectors,
        (selector: SegmentSelector) => checkedSegments.includes(selector.id)
    );

    return map(
        checkedSegmentList,
        (selector: SegmentSelector) => selector[val]
    );
};

export const getPreviewSegment = (
    segmentsGroup: CryptaSegmentGroup,
    checkedSegments: string[]
): SegmentCategoryText[] => {
    return Object.keys(segmentsGroup).reduce(
        (segments: SegmentCategoryText[], key: string) => {
            const segment = segmentsGroup[key];
            const segmentsItems = getCheckedSegmentsVal(
                segment,
                'name',
                checkedSegments
            );

            if (segmentsItems.length > 0) {
                const count =
                    segment.title.length +
                    segmentsItems.reduce(
                        (countExtra: number, element: string) =>
                            countExtra + element.length,
                        0
                    );

                segments.push({
                    title: segment.title,
                    segments: segmentsItems,
                    count
                });
            }

            return segments;
        },
        []
    );
};

export const getPreviewText = (
    textPreview: SegmentCategoryText[],
    BLOCK: string,
    hideSubtitle?: boolean
): JSX.Element | null => {
    if (textPreview.length <= 0) {
        return null;
    }

    return (
        <>
            {textPreview.map((segment: SegmentCategoryText, index: number) => (
                <>
                    {hideSubtitle ||
                        (segment.title && (
                            <span className={Bem.cls(BLOCK, 'subtitle')}>
                                {Boolean(index) && ' · '}
                                {`${segment.title}: `}
                            </span>
                        ))}
                    {segment.segments.join(', ')}
                </>
            ))}
        </>
    );
};

export const getPreviewTextCompact = (
    textPreview: SegmentCategoryText[],
    limits: number,
    BLOCK: string,
    hideSubtitle?: boolean
): {
    hide: boolean;
    shortContent: JSX.Element;
} => {
    const result: JSX.Element[] = [];

    const hideDot = textPreview.every(
        (segment: SegmentCategoryText, index: number) => {
            const { count = 0 } = segment;
            const segments = segment.segments.join(', ');

            limits -= count + SIGNS_COMPACT;

            result.push(
                <>
                    {hideSubtitle ||
                        (segment.title && (
                            <span className={Bem.cls(BLOCK, 'subtitle')}>
                                {Boolean(index) && ' · '}
                                {`${segment.title}: `}
                            </span>
                        ))}
                    {limits > 0 ? segments : segments.slice(0, limits)}
                </>
            );

            return limits > 0;
        }
    );

    return {
        hide: hideDot,
        shortContent: (
            <>
                {result}
                {!hideDot && '...'}
            </>
        )
    };
};

export const transformInterestsToSegments = (
    interestsSets: InterestsSetType[],
    interestsIds: InterestsIdsType
): SegmentCategoryText[] => {
    return interestsSets.reduce(
        (
            acc: SegmentCategoryText[],
            interest: InterestsSetType,
            key: number
        ) => {
            const prefixText = i18n(interest.period);
            const interests = `${i18n('interests-set')} ${key + 1}`;
            const text = interest.checkedList.map(id => interestsIds[id].name);
            const countText = text.reduce(
                (countExtra: number, element: string) =>
                    countExtra + element.length,
                0
            );

            if (text.length !== 0) {
                if (
                    interestsSets.length === 1 &&
                    interest.period === CryptaInterestTypeWeb.all
                ) {
                    acc.push({
                        title: '',
                        segments: text,
                        count: countText
                    });
                } else {
                    const title =
                        interestsSets.length === 1 ? prefixText : interests;

                    acc.push({
                        title,
                        segments: text,
                        count: title.length + countText
                    });
                }
            }

            return acc;
        },
        []
    );
};

export const getSortedCheckedInterests = (
    checkedInterestsId: string[],
    interestsTree: InterestsTreeType,
    parentId: string,
    sortedIds: string[]
): string[] => {
    const interests = interestsTree[parentId];

    if (interests && interests.length) {
        interests.forEach(interest => {
            const interestId = interest.id;

            if (checkedInterestsId.includes(interestId)) {
                sortedIds.push(interestId);
            }

            getSortedCheckedInterests(
                checkedInterestsId,
                interestsTree,
                interestId,
                sortedIds
            );
        });
    }

    return sortedIds;
};

export const getPredictorPreviewText = (
    segmentsGroup: CryptaSegmentGroup,
    checkedSegments?: string[]
): TextPredictor[] => {
    const result: TextPredictor[] = [];

    if (!checkedSegments) {
        return result;
    }

    const textPreviewRaw = map(segmentsGroup, (segment: SegmentCategory) => {
        segment.segments = getCheckedSegmentsVal(
            segment,
            'name',
            checkedSegments
        );

        return segment;
    });
    const textPreview = filter(
        textPreviewRaw,
        (segment: SegmentCategory) => segment.segments.length
    );

    if (textPreview && textPreview.length) {
        map(textPreview, (segment: SegmentCategory) => {
            result.push({
                title: segment.title,
                value: segment.segments.join(', ')
            });
        });
    }

    return result;
};

export const getPredictorInterestsText = (
    interestsIds: InterestsIdsType,
    interestsSets?: InterestsSetType[]
): TextPredictor[] => {
    if (!interestsSets) {
        return [];
    }

    return interestsSets.reduce(
        (acc: TextPredictor[], interest: InterestsSetType, key: number) => {
            const ids = map(interest.checkedList, id => id);
            const text = ids.map(id => interestsIds[id].name).join(', ');

            if (text.length !== 0) {
                acc.push({
                    title: getUserProfileConditionTitle(
                        '',
                        UserProfileRetargetingConditionGoalType.INTERESTS,
                        interest.period
                    ),
                    value: text
                });
            }

            return acc;
        },
        []
    );
};

export const calculateOpenedCategory = (
    groupVal: DataForEditGroups
): Action[] => {
    const categoryPriorityOrder = [
        AudienceTargetingsType.socialDemo,
        AudienceTargetingsType.family,
        AudienceTargetingsType.behaviors,
        AudienceTargetingsType.interests
    ];
    const categoryActions = {
        socialDemo: openCryptaSocialDemoEditor,
        family: openCryptaGroupFamilyEditor,
        behaviors: openCryptaBehavioralEditor,
        interests: openInterestsTargetingEditor
    };

    return reduce(
        groupVal.groups,
        (acc: Action[], group: EditGroupData) => {
            const isFound = find(categoryPriorityOrder, (category: string) => {
                const categoryVal = get(
                    group.value,
                    `audienceTargetings.${category}`
                );

                if (categoryVal && categoryVal.length > 0) {
                    acc.push(categoryActions[category](group.id, categoryVal));

                    return true;
                }

                return false;
            });

            if (!isFound) {
                acc.push(openCryptaSocialDemoEditor(group.id, []));
            }

            return acc;
        },
        []
    );
};

export const getPredictorMetricsText = (
    conditionData?: RetargetingConditionData
): TextPredictor[] => {
    if (!conditionData) {
        return [];
    }

    const rulesGroups = conditionData.rulesGroups;
    const goals = conditionData.goals || [];

    return rulesGroups.reduce(
        (res: TextPredictor[], group: RetargetingConditionDataRulesGroup) => {
            const resultDescription = group.items
                .map(item => {
                    if (!item.id) {
                        return '';
                    }

                    const goal = goals[item.id];

                    if (!goal) {
                        return `${i18n('unavailable-goal')} (id:${item.id})`;
                    }

                    const domain =
                        goal.domain !== undefined ? `${goal.domain} :: ` : '';
                    const description = `${domain}${goal.name}`;

                    if (
                        item.type ===
                        RetargetingConditionRuleType.METRIKA_GOAL_RULE
                    ) {
                        return item.period !== undefined
                            ? i18n('description-by-day', {
                                  count: item.period,
                                  description
                              })
                            : description;
                    }

                    return description;
                })
                .join(', ');

            res.push({
                title: getRetargetingConditionTypeText(group.type),
                value: resultDescription
            });

            return res;
        },
        []
    );
};
